import {PermissionType} from '@hconnect/apiclient'
import {ContentBox, ContentMode} from '@hconnect/common/components/ContentBox'
import {
  EquipmentRunningTimes,
  DowntimeStoppageCodes,
  GetStackedBarChartComponent
} from '@hconnect/common/types'
import {checkPermission, usePermissionsParsedQuery} from '@hproduce/permissions'
import DashboardIcon from '@mui/icons-material/Dashboard'
import {Box, Button, Link} from '@mui/material'
import {EquipmentRunningTimesResult, OperatingHoursLegendList} from '@widgets/running-times'
import {FC, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

type RunningTimesSummaryProps = {
  contentMode: ContentMode
  isLoading?: boolean
  runningTimePerformances?: EquipmentRunningTimes[]
  getStackedBarChartComponent?: GetStackedBarChartComponent
  isError: boolean
  stoppageCodes: DowntimeStoppageCodes
  refetchRunningTimePerformance: () => void
  plantContext: {plantId: string; countryId: string}
  plantTimeZone: string
  dashboardUrl: string
  onEquipmentClick: (equipmentId: string) => void
}

export const RunningTimesSummary: FC<RunningTimesSummaryProps> = ({
  contentMode,
  isLoading,
  runningTimePerformances,
  getStackedBarChartComponent,
  isError,
  stoppageCodes,
  refetchRunningTimePerformance,
  plantContext,
  plantTimeZone,
  dashboardUrl,
  onEquipmentClick
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data: permissions} = usePermissionsParsedQuery()

  const hasPermission = useCallback(
    (permissionType: PermissionType) =>
      permissions
        ? checkPermission(permissionType, plantContext.plantId, permissions, [
            plantContext.countryId
          ])
        : false,
    [plantContext.plantId, plantContext.countryId, permissions]
  )

  const errorMessage = isError ? t('error.label.fetchingError') : undefined

  return (
    <ContentBox
      title={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          {t('summary.label.runningTimes')}
          <Link sx={{textDecoration: 'none'}} href={dashboardUrl} data-test-id="dashboard-link">
            <Button
              variant="text"
              startIcon={<DashboardIcon />}
              sx={{width: '200px', height: '58px'}}
            >
              {t('goToDashboard')}
            </Button>
          </Link>
        </Box>
      }
      mode={contentMode}
      bodyWithPadding
      isLoading={isLoading}
      errorMessage={errorMessage}
      minHeight="450px"
      data-test-id="running-times-container"
    >
      <OperatingHoursLegendList />
      {runningTimePerformances?.map(
        (equipmentPerformanceData: EquipmentRunningTimes, index: number) => {
          return (
            <EquipmentRunningTimesResult
              equipment={equipmentPerformanceData.equipment}
              runningTimePerformance={equipmentPerformanceData}
              key={equipmentPerformanceData.equipment.id}
              getStackedBarChartComponent={getStackedBarChartComponent}
              hideDivider={runningTimePerformances?.length === index + 1}
              plantId={plantContext.plantId}
              stoppageCodes={stoppageCodes}
              onStoppageCreationSuccess={refetchRunningTimePerformance}
              hasPermission={hasPermission}
              plantTimeZone={plantTimeZone}
              onClick={() => onEquipmentClick(equipmentPerformanceData.equipment.id)}
            />
          )
        }
      )}
    </ContentBox>
  )
}
